import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useCallback, useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useUser } from "@/contexts/UserContext";

interface ResumeUploadModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const AVAILABLE_SECTIONS = [
  "Personal details",
  "Summary",
  "Technical skills",
  "Work experience",
  "Projects",
  "Education",
  "Certifications"
];

const ResumeUploadModal = ({ isOpen, onOpenChange }: ResumeUploadModalProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [hasValidFile, setHasValidFile] = useState(false);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newSection, setNewSection] = useState("");
  const [rawJson, setRawJson] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { userId } = useUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const tryParsing = useCallback(async (filePath: string) => {
    try {
      setIsParsing(true);
      const response = await supabase.functions.invoke('parse-resume', {
        body: {
          filePath: filePath,
          sections: selectedSections,
          userId,
          rawJson: rawJson || undefined
        }
      });
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to parse resume');
      }
      const parsedData = response.data.data;
      console.log('Parsed resume data:', parsedData);

      // Invalidate all relevant queries to refresh the dashboard data
      await queryClient.invalidateQueries({ queryKey: ['developer-profile-details'] });
      await queryClient.invalidateQueries({ queryKey: ['developer-profile-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['developer-profile-company'] });
      await queryClient.invalidateQueries({ queryKey: ['certifications'] });
      await queryClient.invalidateQueries({ queryKey: ['education'] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['work-experience'] });
      await queryClient.invalidateQueries({ queryKey: ['skills'] });

      // Close modal on success
      onOpenChange(false);

    } catch (error) {
      console.error('Error parsing resume:', error);
      toast({
        title: "Upload failed",
        description: "There was an error processing your resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsParsing(false);
    }
  }, [selectedSections, userId, onOpenChange, toast, queryClient, rawJson]);

  const handleFileUpload = useCallback(async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const fileName = `${user.id}/${file.name}`;

      const { error: uploadError, data } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('developer_profiles')
        .upsert({
          user_id: user.id,
          resume_url: publicUrl,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Start parsing immediately after successful upload
      await tryParsing(fileName);

      toast({
        title: "Success",
        description: "Your resume has been uploaded.",
      });

    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  }, [toast, tryParsing]);

  const handleAddSection = (section: string) => {
    if (section === "All sections") {
      const unselectedSections = AVAILABLE_SECTIONS.filter(s => !selectedSections.includes(s));
      setSelectedSections([...selectedSections, ...unselectedSections]);
    } else if (!selectedSections.includes(section)) {
      setSelectedSections([...selectedSections, section]);
    }
    setNewSection("");
    setShowSuggestions(false);
  };

  const handleRemoveSection = (section: string) => {
    setSelectedSections(selectedSections.filter(s => s !== section));
  };

  const handleInputClick = () => {
    setShowSuggestions(prev => !prev);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-visible"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}>
        <DialogHeader>
          <DialogTitle>Upload Resume</DialogTitle>
          <DialogDescription>
            Upload your resume in PDF, DOC, or DOCX format, or paste raw JSON data. Maximum file size is 5MB.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              disabled={isUploading || isParsing}
              onChange={(newFile) => setHasValidFile(newFile.target.files.length > 0)}
              className="cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
            />
            {(isUploading || isParsing) &&
              <p className="text-sm text-muted-foreground pt-2">
                {isParsing ? "Parsing... (could take up to 2 minutes)" : "Uploading..."}
              </p>
            }
          </div>

          <div className="hidden">
            <Label className="text-sm font-medium mb-1 block">
              Sections to automatically update from upload
            </Label>
            <div className="relative" ref={dropdownRef}>
              <Input
                value={newSection}
                onChange={(e) => {
                  setNewSection(e.target.value);
                  setShowSuggestions(true);
                }}
                onClick={handleInputClick}
                placeholder="Add section..."
              />
              {showSuggestions && (
                <div className="absolute w-full z-50">
                  <Command className="rounded-lg border shadow-md">
                    <CommandList>
                      <CommandEmpty>No section found.</CommandEmpty>
                      <CommandGroup className="max-h-[200px] overflow-auto">
                        {selectedSections.length < AVAILABLE_SECTIONS.length && (
                          <CommandItem
                            value="All sections"
                            onSelect={handleAddSection}
                            className="cursor-pointer font-medium"
                          >
                            All sections
                          </CommandItem>
                        )}
                        {AVAILABLE_SECTIONS
                          .filter(section =>
                            section.toLowerCase().includes(newSection.toLowerCase()) &&
                            !selectedSections.includes(section)
                          )
                          .map((section) => (
                            <CommandItem
                              key={section}
                              value={section}
                              onSelect={handleAddSection}
                              className="cursor-pointer"
                            >
                              {section}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedSections.map((section) => (
                <Badge
                  key={section}
                  variant="secondary"
                  className="cursor-pointer group"
                  onClick={() => handleRemoveSection(section)}
                >
                  {section} <span className="group-hover:text-red-500 pl-1">×</span>
                </Badge>
              ))}
            </div>
          </div>

        </div>
        <DialogFooter className="flex justify-between sm:justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUploading || isParsing}
          >
            Close
          </Button>
          <Button
            type="submit"
            onClick={handleFileUpload}
            disabled={isUploading || isParsing || (!hasValidFile && !rawJson)}
          >
            {isParsing ? "Parsing..." : isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResumeUploadModal;
